import { roundNumberToDecimal } from '@monetize/utils/core';

export interface CalculateOfferingCustomDiscountParams {
  newValue: number;
  isSequential: boolean;
  hasOfferingLevelDiscount: boolean;
  amountBeforeDiscount: number;
  amountAfterCatalogDiscount: number;
  weightedTotalCatalogDiscount: number;
}

/**
 * Derives the custom discount % for an offering from a target total amount.
 *
 * SEQUENTIAL (or no catalog discount): catalog discount base is the
 * post-custom amount, so the catalog portion applies only to the remaining.
 * COMBINED with catalog: derive total effective discount from original price,
 * then subtract the catalog portion to isolate the custom discount.
 */
export function calculateOfferingCustomDiscount({
  newValue,
  isSequential,
  hasOfferingLevelDiscount,
  amountBeforeDiscount,
  amountAfterCatalogDiscount,
  weightedTotalCatalogDiscount,
}: CalculateOfferingCustomDiscountParams): number {
  if (newValue <= 0 || newValue >= amountAfterCatalogDiscount) {
    return 0;
  }

  if (isSequential || !hasOfferingLevelDiscount) {
    return (
      ((amountAfterCatalogDiscount - newValue) / amountAfterCatalogDiscount) *
      100
    );
  }

  const totalEffective =
    ((amountBeforeDiscount - newValue) / amountBeforeDiscount) * 100;
  return Math.max(0, totalEffective - weightedTotalCatalogDiscount);
}

export interface CalculateTargetAmountParams {
  newValue: number;
  scope: 'TOTAL_AMOUNT' | 'UNIT_PRICE';
  isSequential: boolean;
  hasCatalogDiscount: boolean;
  amountWithoutDiscount: number;
  amountAfterCatalogDiscount: number;
  displayUnitPrice: number;
  unitPriceAfterCatalogDiscount: number;
  catalogDiscountTotal: number;
}

export interface CalculateTargetAmountResult {
  discount: number;
  mirroredValue: number;
}

/**
 * Derives the custom discount % and the mirrored field value from a target
 * amount or unit price entered by the user.
 *
 * SEQUENTIAL: each discount step applies to the already-reduced price, so
 * the catalog discount base is the price after custom discount.
 * COMBINED: catalog and custom discounts both apply to the original price,
 * so the custom portion is derived from the total effective discount.
 */
export function calculateTargetAmount({
  newValue,
  scope,
  isSequential,
  hasCatalogDiscount,
  amountWithoutDiscount,
  amountAfterCatalogDiscount,
  displayUnitPrice,
  unitPriceAfterCatalogDiscount,
  catalogDiscountTotal,
}: CalculateTargetAmountParams): CalculateTargetAmountResult {
  let discount = 0;

  if (scope === 'TOTAL_AMOUNT') {
    const base = hasCatalogDiscount
      ? amountAfterCatalogDiscount
      : amountWithoutDiscount;

    if (newValue > 0 && newValue < base) {
      if (isSequential || !hasCatalogDiscount) {
        discount = ((base - newValue) / base) * 100;
      } else {
        const totalEffective =
          ((amountWithoutDiscount - newValue) / amountWithoutDiscount) * 100;
        discount = Math.max(0, totalEffective - catalogDiscountTotal);
      }
    }

    const mirroredValue = roundNumberToDecimal(
      isSequential || !hasCatalogDiscount
        ? unitPriceAfterCatalogDiscount * (1 - discount / 100)
        : displayUnitPrice * (1 - (catalogDiscountTotal + discount) / 100),
      10,
    );
    return { discount, mirroredValue };
  } else {
    const base = hasCatalogDiscount
      ? unitPriceAfterCatalogDiscount
      : displayUnitPrice;

    if (newValue > 0 && newValue < base) {
      if (isSequential || !hasCatalogDiscount) {
        discount = ((base - newValue) / base) * 100;
      } else {
        const totalEffective =
          ((displayUnitPrice - newValue) / displayUnitPrice) * 100;
        discount = Math.max(0, totalEffective - catalogDiscountTotal);
      }
    }

    const mirroredValue = roundNumberToDecimal(
      isSequential || !hasCatalogDiscount
        ? amountAfterCatalogDiscount * (1 - discount / 100)
        : amountWithoutDiscount * (1 - (catalogDiscountTotal + discount) / 100),
    );
    return { discount, mirroredValue };
  }
}
